import { publish } from "./events";

/**
 * Session config + cookie-aware fetch wrapper for the Talis Auth cookie model.
 *
 * The browser never sees the access or refresh token — those live in
 * httpOnly cookies set by the auth service. This module exposes:
 *
 *   - setSessionConfig({ authUrl, appId, allowedRedirects })  (required)
 *   - authFetch(path, init)            cookie-bearing fetch with CSRF header
 *   - validateRedirect(url)            allowlist check
 *   - generatePkce()                   { verifier, challenge }
 *   - randomState()                    crypto-random string
 *   - sha256Base64Url(input)
 *   - logoutBroadcast()                cross-tab logout signal
 *   - subscribeLogoutBroadcast(fn)
 *
 * setSessionConfig MUST be called before any other call. authUrl must be
 * https:// (or http://localhost… in dev). appId is required. allowedRedirects
 * is required and must be an array of allowed origin+path prefixes.
 */

const AUTHEVENT_LOGOUT = "auth:logout";
const AUTHEVENT_LOGIN = "auth:login";

const _config = {
  authUrl: null,
  appId: null,
  allowedRedirects: null,
  // origin (e.g. "https://orbit.talis-ai.com") -> appId ("orbit").
  // Used to pick the right app when a 401 bounces off a cross-app endpoint
  // so refresh rotates that app's cookie, not this webapp's default.
  appsByOrigin: {},
  ready: false,
};

let _loggingOut = false;

const CSRF_COOKIE_NAMES = ["__Secure-talis_csrf", "talis_csrf"];
const LOGOUT_BROADCAST_CHANNEL = "talis-auth";

let _broadcastChannel = null;
function _getBroadcastChannel() {
  if (typeof BroadcastChannel === "undefined") return null;
  if (_broadcastChannel === null) {
    _broadcastChannel = new BroadcastChannel(LOGOUT_BROADCAST_CHANNEL);
  }
  return _broadcastChannel;
}

export function setSessionConfig({ authUrl, appId, allowedRedirects, apps } = {}) {
  if (!authUrl) {
    throw new Error("setSessionConfig: authUrl is required");
  }
  if (!/^https:\/\//i.test(authUrl) && !/^http:\/\/localhost(:|\/|$)/i.test(authUrl) && !/^http:\/\/127\.0\.0\.1(:|\/|$)/i.test(authUrl)) {
    throw new Error(
      `setSessionConfig: authUrl must be HTTPS (got ${authUrl}). HTTP only allowed for localhost.`
    );
  }
  if (!appId) {
    throw new Error("setSessionConfig: appId is required");
  }
  if (!Array.isArray(allowedRedirects) || allowedRedirects.length === 0) {
    throw new Error(
      "setSessionConfig: allowedRedirects is required (array of allowed origin+path prefixes)"
    );
  }
  _config.authUrl = authUrl.replace(/\/$/, "");
  _config.appId = appId;
  _config.allowedRedirects = allowedRedirects.slice();
  _config.appsByOrigin = {};
  if (apps && typeof apps === "object") {
    for (const [baseUrl, mappedAppId] of Object.entries(apps)) {
      if (!mappedAppId) continue;
      let origin;
      try {
        origin = new URL(baseUrl).origin;
      } catch {
        throw new Error(`setSessionConfig: invalid app base URL: ${baseUrl}`);
      }
      _config.appsByOrigin[origin] = mappedAppId;
    }
  }
  _config.ready = true;
}

function _requireConfig() {
  if (!_config.ready) {
    throw new Error(
      "talis-auth: setSessionConfig({ authUrl, appId, allowedRedirects }) must be called before any auth operation."
    );
  }
}

export function getConfig() {
  _requireConfig();
  return { authUrl: _config.authUrl, appId: _config.appId };
}

export function isLoggingOut() {
  return _loggingOut;
}

export function beginLogout() {
  _loggingOut = true;
}

export function endLogout() {
  _loggingOut = false;
}

export function triggerLogoutEvent() {
  publish(AUTHEVENT_LOGOUT, {});
}

// --- Redirect allowlist ---------------------------------------------------

export function validateRedirect(url) {
  _requireConfig();
  if (!url) return false;
  let parsed;
  try {
    parsed = new URL(url, window.location.origin);
  } catch {
    return false;
  }
  // Disallow non-http(s) schemes outright (javascript:, data:, etc.)
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    return false;
  }
  for (const allowed of _config.allowedRedirects) {
    let allowedParsed;
    try {
      allowedParsed = new URL(allowed);
    } catch {
      continue;
    }
    if (parsed.origin !== allowedParsed.origin) continue;
    // Allow exact path match or sub-path. Prevents /foo matching /foobar.
    const allowedPath = allowedParsed.pathname || "/";
    const reqPath = parsed.pathname || "/";
    if (
      reqPath === allowedPath ||
      reqPath.startsWith(allowedPath.endsWith("/") ? allowedPath : allowedPath + "/")
    ) {
      return true;
    }
  }
  return false;
}

// --- CSRF + fetch wrapper -------------------------------------------------

function _readCookie(name) {
  const all = document.cookie.split(";");
  for (const raw of all) {
    const [k, ...v] = raw.trim().split("=");
    if (k === name) return decodeURIComponent(v.join("="));
  }
  return null;
}

export function getCsrfToken() {
  for (const name of CSRF_COOKIE_NAMES) {
    const v = _readCookie(name);
    if (v) return v;
  }
  return null;
}

const REFRESH_PATH = "/api/token/refresh";

function _buildRequest(path, init) {
  const url = path.startsWith("http") ? path : `${_config.authUrl}${path}`;
  const method = (init.method || "GET").toUpperCase();
  const headers = new Headers(init.headers || {});
  if (!headers.has("Content-Type") && init.body && typeof init.body === "string") {
    headers.set("Content-Type", "application/json");
  }
  // Attach CSRF token on unsafe methods. Re-read on each attempt so a refresh
  // that rotated the CSRF cookie is reflected on the retried request.
  if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    const csrf = getCsrfToken();
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  return { url, method, headers };
}

/**
 * Determine which appId's session should be refreshed if `path` returns 401.
 *
 * Priority:
 *   1. explicit override (authFetch caller passed `app`)
 *   2. origin of the request URL, matched against the `apps` registry
 *   3. _config.appId (SDK default — preserves legacy single-app behaviour)
 */
function _resolveAppForRequest(path, explicit) {
  if (explicit) return explicit;
  const url = path.startsWith("http") ? path : `${_config.authUrl}${path}`;
  try {
    const origin = new URL(url).origin;
    if (_config.appsByOrigin[origin]) return _config.appsByOrigin[origin];
  } catch {
    // fall through
  }
  return _config.appId;
}

/**
 * Cookie-bearing fetch with CSRF header and optional 401 retry.
 *
 * By default, a 401 triggers a transparent refresh + retry so returning
 * users with an expired access cookie are recovered silently. Pass
 * `{ _skipRetry: true }` to disable this — use it for bootstrap probes
 * (e.g. /api/me on page load) where a 401 means "not logged in", not
 * "expired session", and a retry would waste a refresh call.
 */
export async function authFetch(path, init = {}) {
  _requireConfig();
  const skipRetry = init._skipRetry;
  const doFetch = () => {
    const { url, method, headers } = _buildRequest(path, init);
    return fetch(url, { ...init, method, headers, credentials: "include" });
  };

  let res = await doFetch();

  if (skipRetry) return res;

  // Transparent 401 recovery: if the access cookie has expired but the refresh
  // cookie is still live, rotate it and retry once. Never recurse into the
  // refresh endpoint itself, and never interfere with an active logout.
  const isRefreshCall = path.startsWith(REFRESH_PATH);
  if (res.status === 401 && !isRefreshCall && !_loggingOut) {
    const targetApp = _resolveAppForRequest(path, init.app);
    const refreshed = await tryRefreshToken(targetApp);
    if (refreshed) {
      res = await doFetch();
    }
  }
  return res;
}

// --- PKCE helpers ---------------------------------------------------------

function _b64url(bytes) {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function randomState(length = 32) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return _b64url(bytes);
}

export async function sha256Base64Url(input) {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return _b64url(new Uint8Array(digest));
}

export async function generatePkce() {
  // RFC 7636: verifier is 43..128 chars from [A-Z][a-z][0-9]-._~
  const verifier = randomState(64); // 64 random bytes -> ~86 base64url chars
  const challenge = await sha256Base64Url(verifier);
  return { verifier, challenge };
}

// --- Cross-tab logout -----------------------------------------------------

export function logoutBroadcast() {
  const ch = _getBroadcastChannel();
  if (ch) ch.postMessage({ type: "logout" });
}

export function subscribeLogoutBroadcast(fn) {
  const ch = _getBroadcastChannel();
  if (!ch) return () => {};
  const handler = (e) => {
    if (e?.data?.type === "logout") fn();
  };
  ch.addEventListener("message", handler);
  return () => ch.removeEventListener("message", handler);
}

// --- Refresh dedup --------------------------------------------------------
// Concurrent 401 handlers for the SAME app share a single in-flight promise
// so we don't fan out redundant /api/token/refresh calls. Keyed by appId so
// separate apps refresh independently.
const _refreshPromises = new Map(); // appId -> Promise<{ok,status,transient}>

/**
 * Rotate the refresh cookie for `appId` (defaults to the SDK-configured app).
 * Returns `true`/`false` for legacy callers that just want a boolean; see
 * `refreshSession()` below for callers that need to distinguish a transient
 * failure (network blip, 429, 5xx) from a definitive auth failure (401).
 */
export async function tryRefreshToken(appId) {
  const result = await refreshSession(appId);
  return result.ok;
}

/**
 * Like tryRefreshToken but returns { ok, status, transient }:
 *   ok        — true on 2xx
 *   status    — HTTP status, or 0 for network errors
 *   transient — true when the failure is NOT a definitive auth failure
 *               (network errors, 429, 5xx). Only `transient === false` with
 *               `ok === false` means "session is genuinely dead."
 *
 * Pass `appId` to refresh a specific app's session; omit for the SDK default.
 */
export async function refreshSession(appId) {
  _requireConfig();
  if (_loggingOut) return { ok: false, status: 0, transient: true };
  const targetApp = appId || _config.appId;
  if (_refreshPromises.has(targetApp)) return _refreshPromises.get(targetApp);
  const promise = (async () => {
    try {
      const res = await authFetch(`/api/token/refresh?app=${encodeURIComponent(targetApp)}`, { method: "POST" });
      const transient = !res.ok && (res.status === 429 || res.status >= 500);
      return { ok: res.ok, status: res.status, transient };
    } catch {
      return { ok: false, status: 0, transient: true };
    } finally {
      _refreshPromises.delete(targetApp);
    }
  })();
  _refreshPromises.set(targetApp, promise);
  return promise;
}

export { AUTHEVENT_LOGOUT, AUTHEVENT_LOGIN };
